import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getEntities,
  getEntity,
  createEntity,
  updateEntity,
  deleteEntity,
  getSeizures,
  getSeizure,
  createSeizure,
  updateSeizure,
  deleteSeizure,
  linkEntityToSeizure,
  unlinkEntityFromSeizure,
  linkProductToSeizure,
  unlinkProductFromSeizure,
  linkItemToSeizure,
  unlinkItemFromSeizure,
  linkSellerToSeizure,
  unlinkSellerFromSeizure,
  getSeizureActivity,
  getProducts,
  getProduct,
  createProduct,
  updateProduct,
  deleteProduct,
  getCustomEntitiesStats,
  linkObjectToEntity,
  unlinkObjectFromEntity,
  type EntityLinkKind,
} from '../api/react/CustomEntities/customEntities'
import { getItems } from '../api/react/items/Items'
import { getSellers } from '../api/react/sellers/Sellers'
import { getSAW } from '../api/react/saw/saw'
import type { CreateEntityPayload, UpdateEntityPayload, CreateSeizurePayload, UpdateSeizurePayload, CreateProductPayload, UpdateProductPayload, CustomEntitiesStatsParams, LinkSearchType } from '../types/customEntities.types'

export const CUSTOM_ENTITY_KEYS = {
  list: (search?: string, type?: string) => ['entities', search, type] as const,
  detail: (id: string) => ['entity', id] as const,
  stats: (params: CustomEntitiesStatsParams) => ['krakenStats', params] as const,
}

export const SEIZURE_KEYS = {
  list: (search?: string) => ['seizures', search] as const,
  detail: (id: string) => ['seizure', id] as const,
  activity: (id: string) => ['seizureActivity', id] as const,
}

export const PRODUCT_KEYS = {
  list: (search?: string) => ['products', search] as const,
  detail: (id: string) => ['product', id] as const,
}

// --- Entities ---

export const useGetEntities = (search: string, type: string) => {
  return useInfiniteQuery({
    queryKey: CUSTOM_ENTITY_KEYS.list(search, type),
    queryFn: ({ pageParam = 1 }) =>
      getEntities({
        page: pageParam as number,
        page_size: 200,
        ordering: '-created_at',
        ...(search ? { search } : {}),
        ...(type !== 'all' ? { type } : {}),
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage?.hasNext ? lastPage.nextPage : undefined),
    refetchOnWindowFocus: false,
    staleTime: 15_000,
  })
}

export const useGetEntity = (id: string) => {
  return useQuery({
    queryKey: CUSTOM_ENTITY_KEYS.detail(id),
    queryFn: () => getEntity(id),
    enabled: !!id,
  })
}

export const useCreateEntity = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateEntityPayload) => createEntity(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entities'] })
      queryClient.invalidateQueries({ queryKey: ['entitiesOverview'] })
    },
  })
}

export const useUpdateEntity = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateEntityPayload }) => updateEntity(id, data),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: CUSTOM_ENTITY_KEYS.detail(id) })
      queryClient.invalidateQueries({ queryKey: ['entities'] })
    },
  })
}

export const useDeleteEntity = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteEntity(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entities'] })
      queryClient.invalidateQueries({ queryKey: ['entitiesOverview'] })
    },
  })
}

// --- Seizures ---

export const useGetSeizures = (search: string) => {
  return useInfiniteQuery({
    queryKey: SEIZURE_KEYS.list(search),
    queryFn: ({ pageParam = 1 }) =>
      getSeizures({
        page: pageParam as number,
        page_size: 30,
        ordering: '-created_at',
        ...(search ? { search } : {}),
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage?.hasNext ? lastPage.nextPage : undefined),
    refetchOnWindowFocus: false,
    staleTime: 15_000,
  })
}

export const useGetSeizure = (id: string) => {
  return useQuery({
    queryKey: SEIZURE_KEYS.detail(id),
    queryFn: () => getSeizure(id),
    enabled: !!id,
  })
}

export const useCreateSeizure = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateSeizurePayload) => createSeizure(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seizures'] })
      queryClient.invalidateQueries({ queryKey: ['seizuresOverview'] })
    },
  })
}

export const useUpdateSeizure = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSeizurePayload }) => updateSeizure(id, data),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: SEIZURE_KEYS.detail(id) })
      queryClient.invalidateQueries({ queryKey: ['seizures'] })
    },
  })
}

export const useDeleteSeizure = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteSeizure(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seizures'] })
      queryClient.invalidateQueries({ queryKey: ['seizuresOverview'] })
    },
  })
}

export const useGetSeizureActivity = (id: string) => {
  return useQuery({
    queryKey: SEIZURE_KEYS.activity(id),
    queryFn: () => getSeizureActivity(id),
    enabled: !!id,
  })
}

export const useLinkEntityToSeizure = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ seizureId, entityId }: { seizureId: string; entityId: string }) =>
      linkEntityToSeizure(seizureId, entityId),
    onSuccess: (_data, { seizureId }) => {
      queryClient.invalidateQueries({ queryKey: SEIZURE_KEYS.detail(seizureId) })
    },
  })
}

export const useUnlinkEntityFromSeizure = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ seizureId, entityId }: { seizureId: string; entityId: string }) =>
      unlinkEntityFromSeizure(seizureId, entityId),
    onSuccess: (_data, { seizureId }) => {
      queryClient.invalidateQueries({ queryKey: SEIZURE_KEYS.detail(seizureId) })
    },
  })
}

export const useLinkProductToSeizure = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ seizureId, productId }: { seizureId: string; productId: string }) =>
      linkProductToSeizure(seizureId, productId),
    onSuccess: (_data, { seizureId }) => {
      queryClient.invalidateQueries({ queryKey: SEIZURE_KEYS.detail(seizureId) })
    },
  })
}

export const useUnlinkProductFromSeizure = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ seizureId, productId }: { seizureId: string; productId: string }) =>
      unlinkProductFromSeizure(seizureId, productId),
    onSuccess: (_data, { seizureId }) => {
      queryClient.invalidateQueries({ queryKey: SEIZURE_KEYS.detail(seizureId) })
    },
  })
}

export const useLinkItemToSeizure = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ seizureId, itemId }: { seizureId: string; itemId: string }) =>
      linkItemToSeizure(seizureId, itemId),
    onSuccess: (_data, { seizureId }) => {
      queryClient.invalidateQueries({ queryKey: SEIZURE_KEYS.detail(seizureId) })
    },
  })
}

export const useUnlinkItemFromSeizure = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ seizureId, itemId }: { seizureId: string; itemId: string }) =>
      unlinkItemFromSeizure(seizureId, itemId),
    onSuccess: (_data, { seizureId }) => {
      queryClient.invalidateQueries({ queryKey: SEIZURE_KEYS.detail(seizureId) })
    },
  })
}

export const useLinkSellerToSeizure = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ seizureId, sellerId }: { seizureId: string; sellerId: string }) =>
      linkSellerToSeizure(seizureId, sellerId),
    onSuccess: (_data, { seizureId }) => {
      queryClient.invalidateQueries({ queryKey: SEIZURE_KEYS.detail(seizureId) })
    },
  })
}

export const useUnlinkSellerFromSeizure = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ seizureId, sellerId }: { seizureId: string; sellerId: string }) =>
      unlinkSellerFromSeizure(seizureId, sellerId),
    onSuccess: (_data, { seizureId }) => {
      queryClient.invalidateQueries({ queryKey: SEIZURE_KEYS.detail(seizureId) })
    },
  })
}

// --- Products ---

export const useGetProducts = (search: string) => {
  return useInfiniteQuery({
    queryKey: PRODUCT_KEYS.list(search),
    queryFn: ({ pageParam = 1 }) =>
      getProducts({
        page: pageParam as number,
        page_size: 30,
        ordering: '-created_at',
        ...(search ? { search } : {}),
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage?.hasNext ? lastPage.nextPage : undefined),
    refetchOnWindowFocus: false,
    staleTime: 15_000,
  })
}

export const useGetProduct = (id: string) => {
  return useQuery({
    queryKey: PRODUCT_KEYS.detail(id),
    queryFn: () => getProduct(id),
    enabled: !!id,
  })
}

export const useCreateProduct = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ data, photoFile }: { data: CreateProductPayload; photoFile?: File }) =>
      createProduct(data, photoFile),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['productsOverview'] })
    },
  })
}

export const useUpdateProduct = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data, photoFile }: { id: string; data: UpdateProductPayload; photoFile?: File }) =>
      updateProduct(id, data, photoFile),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: PRODUCT_KEYS.detail(id) })
      queryClient.invalidateQueries({ queryKey: ['products'] })
    },
  })
}

export const useDeleteProduct = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['productsOverview'] })
    },
  })
}

// --- Link Search ---

export const useSearchForLink = (type: LinkSearchType, search: string, enabled: boolean) => {
  return useQuery({
    queryKey: ['seizureLinkSearch', type, search],
    queryFn: () => {
      switch (type) {
        case 'entity':
          return getEntities({ page: 1, page_size: 10, search })
        case 'product':
          return getProducts({ page: 1, page_size: 10, search })
        case 'item':
          return getItems({ page: 1, page_size: 10, search })
        case 'seller':
          return getSellers({ page: 1, page_size: 10, search })
        case 'saw':
          return getSAW({ page: 1, page_size: 10, search } as Parameters<typeof getSAW>[0])
      }
    },
    enabled: search.length > 0 && enabled,
  })
}

// --- Entity ↔ Ocean object links (items / sellers / sites) ---

const LINKED_KEY: Record<EntityLinkKind, 'items' | 'sellers' | 'saws'> = {
  item: 'items',
  seller: 'sellers',
  saw: 'saws',
}

// Patch every cached ['entities', ...] list so the open drawer reflects a
// link/unlink instantly, without waiting on the (page_size=200) refetch.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const patchEntityLinks = (queryClient: any, entityId: string, kind: EntityLinkKind, mutate: (arr: any[]) => any[]) => {
  const listKey = LINKED_KEY[kind]
  queryClient.setQueriesData({ queryKey: ['entities'] }, (old: any) => {
    if (!old?.pages) return old
    return {
      ...old,
      pages: old.pages.map((page: any) => ({
        ...page,
        data: (page?.data ?? []).map((e: any) =>
          e.id === entityId
            ? { ...e, linked_objects: { ...e.linked_objects, [listKey]: mutate(e.linked_objects?.[listKey] ?? []) } }
            : e,
        ),
      })),
    }
  })
}

export const useLinkObjectToEntity = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ entityId, kind, objectId }: { entityId: string; kind: EntityLinkKind; objectId: string; object?: any }) =>
      linkObjectToEntity(entityId, kind, objectId),
    // Optimistically add the row so the drawer updates immediately.
    onMutate: async ({ entityId, kind, object }) => {
      await queryClient.cancelQueries({ queryKey: ['entities'] })
      const previous = queryClient.getQueriesData({ queryKey: ['entities'] })
      if (object) {
        patchEntityLinks(queryClient, entityId, kind, (arr) =>
          arr.some((o) => String(o.id) === String(object.id)) ? arr : [...arr, object],
        )
      }
      return { previous }
    },
    onError: (_err, _vars, context) => {
      // Roll back on failure.
      context?.previous?.forEach(([key, data]: [any, any]) => queryClient.setQueryData(key, data))
    },
    onSettled: (_data, _err, { entityId }) => {
      queryClient.invalidateQueries({ queryKey: ['entities'] })
      queryClient.invalidateQueries({ queryKey: ['entity', entityId] })
      queryClient.invalidateQueries({ queryKey: ['entityLinks'] })
    },
  })
}

export const useUnlinkObjectFromEntity = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ entityId, kind, objectId }: { entityId: string; kind: EntityLinkKind; objectId: string }) =>
      unlinkObjectFromEntity(entityId, kind, objectId),
    // Optimistically remove the row so the drawer updates immediately.
    onMutate: async ({ entityId, kind, objectId }) => {
      await queryClient.cancelQueries({ queryKey: ['entities'] })
      const previous = queryClient.getQueriesData({ queryKey: ['entities'] })
      patchEntityLinks(queryClient, entityId, kind, (arr) => arr.filter((o) => String(o.id) !== String(objectId)))
      return { previous }
    },
    onError: (_err, _vars, context) => {
      context?.previous?.forEach(([key, data]: [any, any]) => queryClient.setQueryData(key, data))
    },
    onSettled: (_data, _err, { entityId }) => {
      queryClient.invalidateQueries({ queryKey: ['entities'] })
      queryClient.invalidateQueries({ queryKey: ['entity', entityId] })
      queryClient.invalidateQueries({ queryKey: ['entityLinks'] })
    },
  })
}

// Entities linked to a given Ocean object — for the item/seller/site detail pages.
export const useEntitiesLinkedToObject = (kind: EntityLinkKind, objectId?: string) => {
  return useQuery({
    queryKey: ['entityLinks', kind, objectId],
    queryFn: () => getEntities({ page: 1, page_size: 50, [kind]: objectId }),
    enabled: !!objectId,
  })
}

// --- Overview (recent, page=1, page_size=5) ---

export const useGetEntitiesOverview = () => {
  return useQuery({
    queryKey: ['entitiesOverview'],
    queryFn: () => getEntities({ page: 1, page_size: 5, ordering: '-created_at' }),
    staleTime: 60_000,
  })
}

export const useGetSeizuresOverview = () => {
  return useQuery({
    queryKey: ['seizuresOverview'],
    queryFn: () => getSeizures({ page: 1, page_size: 5, ordering: '-created_at' }),
    staleTime: 60_000,
  })
}

export const useGetProductsOverview = () => {
  return useQuery({
    queryKey: ['productsOverview'],
    queryFn: () => getProducts({ page: 1, page_size: 5, ordering: '-created_at' }),
    staleTime: 60_000,
  })
}

// --- Stats ---

export const useGetCustomEntitiesStats = (params: CustomEntitiesStatsParams) => {
  return useQuery({
    queryKey: CUSTOM_ENTITY_KEYS.stats(params),
    queryFn: () => getCustomEntitiesStats(params),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })
}
